import React, { useState, useEffect, useRef } from 'react';
import { Upload, Download, Globe, FileText, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { logAnalyticsEvent } from './firebase';

// Use environment variable with fallback for development
const API_BASE = 'https://srt-translator-srt-translator-backend-418499296729.asia-northeast1.run.app/api';
console.log(API_BASE);
function App() {
  const [file, setFile] = useState(null);
  const [sourceLanguage, setSourceLanguage] = useState('auto');
  const [targetLanguage, setTargetLanguage] = useState('English');
  const [languages, setLanguages] = useState([
    { code: 'auto', name: 'Auto-detect' },
    { code: 'English', name: 'English' },
    { code: 'Spanish', name: 'Spanish' },
    { code: 'French', name: 'French' },
    { code: 'German', name: 'German' },
    { code: 'Italian', name: 'Italian' },
    { code: 'Portuguese', name: 'Portuguese' },
    { code: 'Russian', name: 'Russian' },
    { code: 'Japanese', name: 'Japanese' },
    { code: 'Korean', name: 'Korean' },
    { code: 'Chinese', name: 'Chinese (Simplified)' },
    { code: 'Arabic', name: 'Arabic' },
    { code: 'Hindi', name: 'Hindi' },
    { code: 'Dutch', name: 'Dutch' },
    { code: 'Polish', name: 'Polish' },
    { code: 'Turkish', name: 'Turkish' },
    { code: 'Swedish', name: 'Swedish' },
    { code: 'Danish', name: 'Danish' },
    { code: 'Norwegian', name: 'Norwegian' },
    { code: 'Finnish', name: 'Finnish' }
  ]);
  const [translation, setTranslation] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastLoggedStatusRef = useRef(null);

  // Load available languages on component mount
  useEffect(() => {
    fetchLanguages();
  }, []);

  useEffect(() => {
    logAnalyticsEvent('page_view', { page_title: 'SRT Translator' });
  }, []);

  // Poll for translation status
  useEffect(() => {
    if (translation?.translationId && translation.status === 'processing') {
      pollIntervalRef.current = setInterval(() => {
        checkTranslationStatus(translation.translationId);
      }, 2000);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [translation?.translationId, translation?.status]);

  const fetchLanguages = async () => {
    try {
      const response = await fetch(`${API_BASE}/languages`);
      const langs = await response.json();
      setLanguages(langs);
    } catch (error) {
      console.error('Failed to load languages:', error);
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.srt')) {
        setError('Please select a valid SRT file');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile?.name.endsWith('.srt')) {
      setFile(droppedFile);
      setError('');
    } else {
      setError('Please drop a valid SRT file');
    }
  };

  const translateFile = async () => {
    if (!file) {
      setError('Please select an SRT file');
      return;
    }

    if (sourceLanguage === targetLanguage && sourceLanguage !== 'auto') {
      setError('Source and target languages cannot be the same');
      return;
    }

    setIsUploading(true);
    setError('');
    setTranslation(null);

    logAnalyticsEvent('translation_start', {
      source_language: sourceLanguage,
      target_language: targetLanguage,
    });

    try {
      const formData = new FormData();
      formData.append('srtFile', file);
      formData.append('sourceLanguage', sourceLanguage);
      formData.append('targetLanguage', targetLanguage);

      const response = await fetch(`${API_BASE}/translate`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Translation failed');
      }

      setTranslation({
        translationId: result.translationId,
        status: 'processing',
        progress: 0,
        total: result.subtitleCount,
        message: 'Starting translation...'
      });

    } catch (error) {
      setError(error.message);
      logAnalyticsEvent('translation_start_failed', {
        error_message: error.message,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const checkTranslationStatus = async (translationId) => {
    try {
      const response = await fetch(`${API_BASE}/translation/${translationId}/status`);
      const status = await response.json();

      if (!response.ok) {
        throw new Error(status.error || 'Failed to check status');
      }

      setTranslation(prev => ({ ...prev, ...status }));

      if (status.status === 'completed' || status.status === 'error') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      }

    } catch (error) {
      console.error('Status check failed:', error);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    }
  };

  const downloadTranslation = async () => {
    if (!translation?.translationId) return;

    try {
      const response = await fetch(`${API_BASE}/translation/${translation.translationId}/download`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = translation.filename || 'translated.srt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      setError(error.message);
    }
  };

  const resetForm = () => {
    setFile(null);
    setTranslation(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    logAnalyticsEvent('translation_reset');
  };

  useEffect(() => {
    if (!translation) {
      lastLoggedStatusRef.current = null;
      return;
    }

    if (translation.status === lastLoggedStatusRef.current) {
      return;
    }

    if (translation.status === 'completed') {
      logAnalyticsEvent('translation_completed', {
        translation_id: translation.translationId,
        subtitle_total: translation.total,
      });
    } else if (translation.status === 'error') {
      logAnalyticsEvent('translation_failed', {
        translation_id: translation.translationId,
        error_message: translation.message,
      });
    }

    lastLoggedStatusRef.current = translation.status;
  }, [translation]);

  const getProgressPercentage = () => {
    if (!translation) return 0;
    return Math.round((translation.progress / translation.total) * 100);
  };

  const getStatusIcon = () => {
    if (!translation) return null;
    
    switch (translation.status) {
      case 'processing':
        return <Loader2 className="animate-spin text-emerald-600" size={20} />;
      case 'completed':
        return <CheckCircle className="text-emerald-700 animate-pulse-slow" size={20} />;
      case 'error':
        return <XCircle className="text-red-600" size={20} />;
      default:
        return <AlertCircle className="text-amber-500 animate-pulse" size={20} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-gradient-to-br from-emerald-700 to-teal-800 rounded-2xl shadow-lg animate-float">
              <Globe className="text-white" size={36} />
            </div>
          </div>
          <h1 className="text-5xl font-extrabold bg-gradient-to-r from-emerald-700 to-teal-700 bg-clip-text text-transparent mb-4">
            AI-Powered SRT Translator
          </h1>
          <p className="text-gray-600 text-lg font-medium">
            Translate YouTube videos and localize video content with our free online subtitle translator.
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-gray-100">
          {/* File Upload Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <div className="p-2 bg-emerald-100 rounded-lg mr-3">
                <FileText className="text-emerald-700" size={24} />
              </div>
              Upload SRT File
            </h2>
            
            <div
              className={`border-2 border-dashed rounded-xl p-10 text-center transition-all duration-300 ${
                file 
                  ? 'border-green-400 bg-gradient-to-br from-green-50 to-emerald-50 shadow-inner cursor-default' 
                  : 'border-gray-300 hover:border-emerald-400 hover:bg-gradient-to-br hover:from-emerald-50 hover:to-teal-50 hover:shadow-md cursor-pointer'
              }`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => !file && fileInputRef.current?.click()}
            >
              {file ? (
                <div className="space-y-3">
                  <div className="inline-flex p-3 bg-emerald-100 rounded-full">
                    <CheckCircle className="text-emerald-700" size={48} />
                  </div>
                  <p className="text-xl font-semibold text-gray-800">{file.name}</p>
                  <p className="text-sm font-medium text-gray-600">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    className="mt-3 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 rounded-lg transition-all duration-200 hover:shadow-md"
                  >
                    Choose different file
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="inline-flex p-4 bg-emerald-50 rounded-full">
                    <Upload className="text-emerald-600" size={48} />
                  </div>
                  <div>
                    <p className="text-lg text-gray-700 font-medium">
                      Drag and drop your SRT file here
                    </p>
                    <p className="text-base text-emerald-700 font-semibold mt-2">
                      or click to browse
                    </p>
                    <p className="text-sm text-gray-500 mt-3 font-medium">
                      Maximum file size: 10MB • .srt format only
                    </p>
                  </div>
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".srt"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Language Selection */}
          <div className="mb-8 grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Source Language
              </label>
              <select
                value={sourceLanguage}
                onChange={(e) => setSourceLanguage(e.target.value)}
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 hover:border-emerald-400 bg-white cursor-pointer appearance-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Target Language
              </label>
              <select
                value={targetLanguage}
                onChange={(e) => setTargetLanguage(e.target.value)}
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 hover:border-emerald-400 bg-white cursor-pointer appearance-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                {languages.filter(lang => lang.code !== 'auto').map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl shadow-sm">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg mr-3">
                  <XCircle className="text-red-600" size={20} />
                </div>
                <p className="text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Translation Status */}
          {translation && (
            <div className="mb-6 p-6 bg-gradient-to-br from-emerald-50/30 to-teal-50/30 rounded-xl border border-emerald-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  {getStatusIcon()}
                  <span className="ml-2 font-semibold text-gray-800">
                    Translation Status
                  </span>
                </div>
                {translation.status === 'processing' && (
                  <span className="text-sm text-gray-600">
                    {translation.batch && translation.totalBatches && 
                      `Batch ${translation.batch}/${translation.totalBatches}`
                    }
                  </span>
                )}
              </div>

              {translation.status === 'processing' && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>{translation.message}</span>
                    <span>{getProgressPercentage()}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-emerald-600 to-teal-700 h-3 rounded-full transition-all duration-300 shadow-sm"
                      style={{ width: `${getProgressPercentage()}%` }}
                    ></div>
                  </div>
                  <div className="text-sm text-gray-600 mt-2">
                    {translation.progress}/{translation.total} subtitles processed
                  </div>
                </div>
              )}

              {translation.status === 'completed' && (
                <div className="space-y-4">
                  <p className="text-emerald-700 font-medium">
                    ✅ Translation completed successfully!
                  </p>
                  <button
                    onClick={downloadTranslation}
                    className="flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-xl hover:from-emerald-700 hover:to-teal-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <Download className="mr-2" size={20} />
                    Download Translated SRT
                  </button>
                </div>
              )}

              {translation.status === 'error' && (
                <p className="text-red-700 font-medium">
                  ❌ Translation failed: {translation.message}
                </p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4">
            <button
              onClick={translateFile}
              disabled={!file || isUploading || (translation?.status === 'processing')}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-emerald-700 to-teal-800 text-white rounded-xl hover:from-emerald-800 hover:to-teal-900 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {isUploading ? (
                <Loader2 className="animate-spin mr-2" size={20} />
              ) : (
                <Globe className="mr-2" size={20} />
              )}
              {isUploading ? 'Uploading...' : 'Start Translation'}
            </button>

            {(translation || file) && (
              <button
                onClick={resetForm}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium"
              >
                Reset
              </button>
            )}
          </div>

          {/* Info Section */}
          <div className="mt-8 p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
            <h3 className="font-bold text-emerald-900 mb-3 flex items-center">
              <div className="p-2 bg-emerald-100 rounded-lg mr-2">
                <AlertCircle className="text-emerald-700" size={20} />
              </div>
              How it works
            </h3>
            <ul className="text-emerald-800 text-sm space-y-2">
              <li className="flex items-start">
                <span className="text-emerald-700 mr-2 font-bold">1.</span>
                <span>Upload your SRT subtitle file</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-700 mr-2 font-bold">2.</span>
                <span>Select source and target languages</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-700 mr-2 font-bold">3.</span>
                <span>AI translates with context awareness</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-700 mr-2 font-bold">4.</span>
                <span>Download your translated SRT file</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm font-medium text-gray-500">
            Hosted by <a href="https://vibehub.co" target="_blank" rel="noopener noreferrer" className="text-gray-700 underline">VibeHub</a> with love.
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Ship Vibes, Not Code
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;
